import axiosInstance from "../../shared/authentication/AxiosInterceptor";
import { normalizeAdminPlanPayload } from "../utils/normalizeAdminPlanPayload";

const BASE = "";
const ADMIN_SUBSCRIPTION_PLANS = "/api/admin/subscription-plans";
const PUBLIC_LANDING = "/api/public";
const STORE_SUBSCRIPTIONS = "/api/store/subscriptions";

/**
 * Response shape from backend: { message, status, data }
 */
const getData = (res) => (res && res.data && res.data.data !== undefined ? res.data.data : res?.data);

// --- Admin: subscription plans (CRUD + status) ---
export const fetchPlans = () =>
  axiosInstance.get(`${BASE}${ADMIN_SUBSCRIPTION_PLANS}`).then((res) => getData(res));

export const fetchPlanById = (planId) =>
  axiosInstance.get(`${BASE}${ADMIN_SUBSCRIPTION_PLANS}/${planId}`).then((res) => getData(res));

export const createPlan = (body) => {
  const payload = normalizeAdminPlanPayload(body, { isUpdate: false });
  console.log("[SubscriptionPlan] create/update payload", payload);
  return axiosInstance.post(`${BASE}${ADMIN_SUBSCRIPTION_PLANS}`, payload).then((res) => getData(res));
};

export const updatePlan = ({ planId, payload }) => {
  const normalized = normalizeAdminPlanPayload(payload, { isUpdate: true });
  console.log("[SubscriptionPlan] create/update payload", normalized);
  return axiosInstance
    .put(`${BASE}${ADMIN_SUBSCRIPTION_PLANS}/${planId}`, normalized)
    .then((res) => getData(res));
};

export const deletePlan = (planId) =>
  axiosInstance.delete(`${BASE}${ADMIN_SUBSCRIPTION_PLANS}/${planId}`).then((res) => getData(res));

export const updatePlanStatus = ({ planId, active }) =>
  axiosInstance
    .patch(`${BASE}${ADMIN_SUBSCRIPTION_PLANS}/${planId}/status`, { active })
    .then((res) => getData(res));

// --- Landing page (no auth): available plans for public website ---
export const fetchPublicPlans = () =>
  axiosInstance.get(`${BASE}${PUBLIC_LANDING}/subscription-plans`).then((res) => getData(res));

// --- Store subscription (Razorpay + current status) ---
export const fetchStoreSubscriptionPlans = (storeId) =>
  axiosInstance.get(`${BASE}${STORE_SUBSCRIPTIONS}/plans/${storeId}`).then((res) => getData(res));

/** Current subscription for store panel (replaces legacy store-product subscription fetch for this flow). */
export const fetchStoreSubscriptionCurrent = (storeId) =>
  axiosInstance.get(`${BASE}${STORE_SUBSCRIPTIONS}/current/${storeId}`).then((res) => getData(res));

/** Legacy store-product subscription row (used by older SubscriptionPlan screen). */
export const fetchStoreSubscriptionLegacy = (storeId) =>
  axiosInstance.get(`${BASE}/store-product/v2/${storeId}/subscription`).then((res) => getData(res));

function buildStoreSubscriptionCheckoutBody({ planId, billingMonths, addOnIds = [] }) {
  const body = { planId, billingMonths };
  const ids = Array.isArray(addOnIds)
    ? addOnIds.filter((id) => id !== null && id !== undefined && id !== "")
    : [];

  if (ids.length) {
    body.addOnIds = ids;
  }

  return body;
}

export const createStoreSubscription = ({ storeId, planId, billingMonths, addOnIds = [] }) =>
  axiosInstance
    .post(
      `${BASE}${STORE_SUBSCRIPTIONS}/create/${storeId}`,
      buildStoreSubscriptionCheckoutBody({ planId, billingMonths, addOnIds })
    )
    .then((res) => getData(res));

/** Non-IN plans: Stripe hosted checkout — returns { checkoutUrl }. */
export const createStripeStoreCheckout = ({ storeId, planId, billingMonths, addOnIds = [] }) =>
  axiosInstance
    .post(
      `${BASE}${STORE_SUBSCRIPTIONS}/stripe/checkout/${storeId}`,
      buildStoreSubscriptionCheckoutBody({ planId, billingMonths, addOnIds })
    )
    .then((res) => getData(res));

export const changeStoreSubscription = ({ storeId, planId, billingMonths, addOnIds = [] }) =>
  axiosInstance
    .post(
      `${BASE}${STORE_SUBSCRIPTIONS}/change/${storeId}`,
      buildStoreSubscriptionCheckoutBody({ planId, billingMonths, addOnIds })
    )
    .then((res) => getData(res));

export const verifyStoreSubscription = ({ storeId, razorpaySubscriptionId }) =>
  axiosInstance
    .post(`${BASE}${STORE_SUBSCRIPTIONS}/verify/${storeId}`, { razorpaySubscriptionId })
    .then((res) => getData(res));

/** Poll target after returning from Stripe hosted Checkout: PENDING / ACTIVE / FAILED row. */
export const fetchStripeCheckoutStatus = ({ storeId, sessionId }) =>
  axiosInstance
    .get(`${BASE}${STORE_SUBSCRIPTIONS}/stripe/checkout-status/${storeId}`, { params: { sessionId } })
    .then((res) => getData(res));

export const cancelStoreSubscription = ({
  storeId,
  subscriptionId,
  cancelAtCycleEnd = true,
}) =>
  axiosInstance
    .post(
      `${BASE}${STORE_SUBSCRIPTIONS}/cancel/${storeId}/${subscriptionId}?cancelAtCycleEnd=${cancelAtCycleEnd}`
    )
    .then((res) => getData(res));

export const assignStoreSubscription = (body) =>
  axiosInstance.put(`${BASE}/store-product/v2/subscription-assign`, body).then((res) => getData(res));

/** @deprecated Use fetchStoreSubscriptionCurrent; kept as alias for thunk name `fetchStoreSubscriptions`. */
export const fetchStoreSubscriptions = (storeId) => fetchStoreSubscriptionCurrent(storeId);

// --- Admin: per-store plan overrides (display / token config) ---
export const updateStoreSubscriptionOverrides = (storeId, body) =>
  axiosInstance
    .post(`${BASE}${ADMIN_SUBSCRIPTION_PLANS}/store-control/${storeId}`, body)
    .then((res) => getData(res));
